/**
 * Every page the app can navigate to by id — the one catalog shared by the
 * command palette (Cmd+K "Go to page") and the tab strips' "+" new-tab picker
 * (components/popout/usePopoutPages). Before TRUS-638 this list lived inside
 * CommandPalette.tsx, so the picker only knew the sidebar's nav-config pages and
 * a page reachable only from the palette (no sidebar entry) could not be opened
 * in a new tab.
 *
 * Ids are the app's `activeTab` keys. `filterNavigablePages` applies the
 * palette's product / role / org / capability gating; callers still check page
 * access (useSidebarPageAccess) where they have it.
 */
import type React from 'react';
import {
  Building,
  LifeBuoy,
  Plus,
  Mail,
  FileText,
  Sparkles,
  Home,
  BarChart3,
  Users,
  Calendar,
  Send,
  Megaphone,
  Globe,
  Target,
  DollarSign,
  Heart,
  MapPin,
  Shield,
  Play,
  Briefcase,
  Brain,
  Network,
  Store,
  Video,
  BookOpen,
  ClipboardList,
  Receipt,
  CreditCard,
  Code,
  Zap,
  UserCircle,
  HeartPulse,
  UserPlus,
  FolderOpen,
  MessageSquare,
  GitBranch,
  ClipboardCheck,
  ShieldCheck,
  FormInput,
  Library,
  Database,
  ArrowLeftRight,
  MessageCircle,
  Radio,
  Bell,
  Eye,
  Headphones,
  UserCog,
  ScanLine,
  Workflow,
  LayoutDashboard,
  ListTodo,
  TrendingUp,
  PlayCircle,
  Settings,
  Share2,
  Phone,
  Gift,
  AlertTriangle,
  Printer,
  Braces,
} from 'lucide-react';
import { isBehavioralAssessmentAllowed } from '@/components/behavioral-assessment/orgGate';

export interface NavigablePage {
  id: string;
  label: string;
  group: string;
  icon: React.ElementType;
  keywords?: string;
  requiresRole?: string[]; // user.role must be in this set (if set)
  requiresOrg?: number[]; // activeOrgId must be in this set (if set)
}

// Mirror of TRIVENY_ALLOWED_TABS in app.tsx — keep in sync. The palette
// uses this to filter out healthcare-only entries when running on
// triveny.ai so the search results stay relevant.
// Triveny-only twin tabs to hide from the WeaveIn "Go to Page" picker. These
// only render under config.isTriveny (see app.tsx), so on WeaveIn they would
// either blank or duplicate an existing entry (platform-roles vs access-control).
export const WEAVEIN_HIDDEN_TAB_PAGE_IDS = new Set<string>(['platform-roles']);

export const TRIVENY_PALETTE_PAGE_IDS = new Set<string>([
  // Home / dashboard — triveny-home is the full-bleed launcher (default
  // landing); platform-dashboard is the My Day surface reached from inside
  // the home. v2-home / home kept for legacy deep-link compat.
  'triveny-home',
  'home',
  'v2-home',
  'platform-dashboard',
  // Unified inbox surfaces
  'workspace-messaging',
  'inbox',
  // Analytics sub-tab inside Marketing
  'platform-marketing-analytics',
  // Triveny modules
  'platform-web-presence',
  'platform-marketing',
  'platform-contacts',
  'platform-sales',
  'platform-revops',
  'platform-invoices',
  'platform-support',
  'platform-intelligence',
  'platform-visitor-id',
  'platform-cold-outreach',
  'platform-lead-builder',
  'platform-marketplace',
  // Unified Outreach workspace (Visitor ID / Lead Builder / Cold Outreach / Funnel)
  'platform-outreach',
  // Tenant admin pages — now Triveny-aware.
  //   • cbo-management: CBOManagement renders a single-row Triveny view
  //     (synth from currentUserCboId) instead of hitting /api/cbos
  //   • org-settings: route auto-defaults settingsOrgId = activeOrgId so
  //     the command-palette quick actions (Branding/Billing/etc.) work
  //   • user-management + client-billing still hit weavein-api /api/cbos
  //     and are kept out; restore once Triveny-side equivalents exist
  'cbo-management',
  'org-settings',
  'user-management',
  // platform-roles is the Triveny equivalent of access-control. We surface
  // ONLY platform-roles in the Triveny palette so the two "Roles & Permissions"
  // entries don't collide; access-control stays available to WeaveIn.
  'platform-roles',
  'nav-layout',
  'data-privacy-settings',
  'security-compliance',
  'external-systems',
  // Settings + profile
  'settings',
  'profile',
  'notifications',
]);

// ── Static page list covering all sidebar navigation items ──
// Users can search any page; the tab handler routes them if they have access.
export const NAVIGABLE_PAGES: NavigablePage[] = [
  // Analytics & Reporting
  { id: 'home', label: 'Home', group: 'Home', icon: Home, keywords: 'dashboard' },
  { id: 'v2-home', label: 'My Day', group: 'Home', icon: Home, keywords: 'home v2 today my day' },
  { id: 'analytics', label: 'Analytics Dashboard', group: 'Analytics', icon: BarChart3 },

  // Member & Case Management
  { id: 'contacts', label: 'Contacts', group: 'Members & Cases', icon: Users },
  { id: 'members', label: 'Members', group: 'Members & Cases', icon: UserCircle },
  // Enrollments (members-by-program roster) — LCC only for now (TODO: productize
  // via nav registry). requiresOrg gates this to activeOrgId === 350.
  { id: 'enrollments-view', label: 'Enrollments', group: 'Members & Cases', icon: ClipboardList, keywords: 'enrollments programs roster members by program', requiresOrg: [350] },
  { id: 'calendar', label: 'Calendar & Scheduling', group: 'Members & Cases', icon: Calendar },
  { id: 'telehealth', label: 'Telehealth Sessions', group: 'Members & Cases', icon: Video },
  { id: 'member-retention', label: 'Member Retention Intelligence', group: 'Members & Cases', icon: HeartPulse },
  { id: 'form-library', label: 'Form Library', group: 'Members & Cases', icon: Library },
  { id: 'workflow-instances', label: 'Workflow Center', group: 'Members & Cases', icon: Play },
  { id: 'contact-types', label: 'Contact Types & Pipelines', group: 'Members & Cases', icon: Users, keywords: 'pipelines contacts' },
  { id: 'event-registrations', label: 'Event Registrations', group: 'Members & Cases', icon: Calendar, keywords: 'events registrations' },
  { id: 'sponsor-retention', label: 'Network Retention Intelligence', group: 'Members & Cases', icon: HeartPulse, keywords: 'sponsor retention network' },

  // Eligibility
  { id: 'eligibility-screening', label: 'Eligibility Pre-Screening', group: 'Eligibility', icon: ClipboardList },
  { id: 'eligibility-verification', label: 'Member Verification', group: 'Eligibility', icon: ShieldCheck },
  { id: 'eligibility-direct', label: 'Direct DHCS/MCP Verification', group: 'Eligibility', icon: Shield },
  { id: 'ffs-transition-cohort', label: 'FFS Transition (1 Jan 2027)', group: 'Eligibility', icon: ShieldCheck, keywords: 'uis ffs fee for service medi-cal restricted emergency scope cohort 2027' },
  { id: 'eligibility-credentials', label: 'Credential Setup', group: 'Eligibility', icon: Settings },

  // Communications
  // TRUS-709: the 2-Way Inbox ('inbox') is retired; the member Inbox lives in the Messaging workspace below.
  { id: 'csr-inbox', label: 'CSR Inbox (Handoffs)', group: 'Communications', icon: Headphones, keywords: 'csr handoff agentic warm transfer renewal help spanish intent' },
  { id: 'groups', label: 'Member Groups', group: 'Communications', icon: Users },
  { id: 'bulk-communications', label: 'Engagements', group: 'Communications', icon: Send, keywords: 'bulk comms' },
  { id: 'campaign-setup', label: 'Campaign Set Up (Engagements + Campaigns + Builder)', group: 'Communications', icon: LayoutDashboard, keywords: 'campaign setup unified tabs engagements campaigns builder journey one place' },
  { id: 'campaigns', label: 'Campaigns', group: 'Communications', icon: Megaphone },
  { id: 'journey-builder', label: 'Campaign Builder', group: 'Communications', icon: GitBranch, keywords: 'journey drag drop reorder steps language pack threshold spanish vietnamese approval preview redetermination flow 1' },
  { id: 'campaign-progress', label: 'Campaign Progress', group: 'Communications', icon: BarChart3, keywords: 'analytics dashboard delivery response opt-out bounce by language by channel weekly auto-report' },
  { id: 'template-library', label: 'Template Library', group: 'Communications', icon: Library, keywords: 'hedis cahps welcome adherence redetermination diabetic eye exam clone' },
  { id: 'referral-automation', label: 'Campaign Automation', group: 'Communications', icon: Workflow, keywords: 'campaign automation referral rules orchestrator agent sdoh housing renewal classifier ai channel' },
  { id: 'chatflows', label: 'Chatflows', group: 'Communications', icon: GitBranch },
  { id: 'interactive-content', label: 'Surveys & Polls', group: 'Communications', icon: ClipboardCheck },
  { id: 'media-library', label: 'Media Library', group: 'Communications', icon: FolderOpen },
  { id: 'whatsapp-templates', label: 'WhatsApp Templates', group: 'Communications', icon: MessageSquare },
  { id: 'workspace-messaging', label: 'Inbox', group: 'Communications', icon: MessageCircle, keywords: 'member inbox conversations messages email sms 2-way messaging workspace channels' },
  { id: 'approvals', label: 'Approvals & Signatures', group: 'Communications', icon: Shield },
  { id: 'whatsapp-business', label: 'WhatsApp Business', group: 'Communications', icon: MessageSquare, keywords: 'whatsapp business api' },
  { id: 'social-media-connections', label: 'Social Media', group: 'Communications', icon: Share2, keywords: 'social media facebook twitter linkedin instagram' },
  { id: 'webinar-management', label: 'Webinar Management', group: 'Communications', icon: Video, keywords: 'webinar events virtual' },
  { id: 'public-site-content', label: 'Public Site Content', group: 'Administration', icon: Globe, keywords: 'public site content blog articles weavein resources web admin marketing site', requiresRole: ['super_admin', 'ops_admin'], requiresOrg: [1] },

  // Activity Tracking — Somos / FUERTES outreach + Hub Meetings &
  // Trainings + reports. CBOs running these programs typically don't
  // have member data in Trust (confidentiality), so this is the
  // primary workspace surface for those tenants.
  { id: 'outreach-activities', label: 'Outreach Activities', group: 'Activity Tracking', icon: ClipboardList, keywords: 'outreach door knocking referral workshop tabling promotora aggregate counts' },
  { id: 'event-tracking', label: 'Hub Meetings & Trainings', group: 'Activity Tracking', icon: Calendar, keywords: 'hub meetings trainings workshops event scheduling attendance' },
  { id: 'activity-reports', label: 'Activity Reports', group: 'Activity Tracking', icon: BarChart3, keywords: 'reports quarterly attendance approval review queue csv xlsx export', requiresRole: ['super_admin', 'org_admin', 'ops_admin', 'program_manager', 'supervisor'] },

  // Emergency
  { id: 'emergency-incidents', label: 'Incident Management', group: 'Emergency', icon: Radio },
  { id: 'emergency-alerts', label: 'External Alerts (FEMA/NWS)', group: 'Emergency', icon: Bell },

  // Network & Collaboration
  { id: 'sponsor-dashboard', label: 'Sponsor Dashboard', group: 'Network', icon: Briefcase },
  { id: 'network-overview', label: 'Network Overview', group: 'Network', icon: Network },
  { id: 'program-marketplace', label: 'Program Marketplace', group: 'Network', icon: Store },
  { id: 'program-intelligence', label: 'Program Intelligence', group: 'Network', icon: Brain },

  // Referrals
  { id: 'help-line', label: 'Help line', group: 'Referrals', icon: LifeBuoy, keywords: 'wren member help line sms text navigator queue follow-up requests approval' },
  { id: 'create-referral', label: 'Create Referrals', group: 'Referrals', icon: Plus },
  { id: 'queue', label: 'Referrals Queue', group: 'Referrals', icon: Users },
  { id: 'unmatched', label: 'Unmatched Referrals', group: 'Referrals', icon: ClipboardList },
  { id: 'matching', label: 'Auto-Match Referrals', group: 'Referrals', icon: Settings },
  { id: 'workspace', label: 'Referral Case Workspace', group: 'Referrals', icon: FileText },

  // Providers
  { id: 'provider-directory', label: 'Provider & Resource Directory', group: 'Providers', icon: MapPin },
  { id: 'directory', label: 'My Providers & Resources', group: 'Providers', icon: Building },

  // Health Plans
  { id: 'health-plans', label: 'Health Plan Management', group: 'Health Plans', icon: Shield },
  { id: 'employer-benefits', label: 'Employer Benefits Portal', group: 'Health Plans', icon: Briefcase },

  // Care Coordination
  { id: 'care-coordination-hub', label: 'Care Coordination Hub', group: 'Care Coordination', icon: Heart },
  { id: 'behavioral-assessment', label: 'Behavioral Assessment', group: 'Care Coordination', icon: ClipboardList, keywords: 'behavioral assessment t-score scale simulation safety crisis questionnaire clinician review provisional norms' },
  { id: 'social-needs-screener', label: 'Social Needs Screener', group: 'Care Coordination', icon: ClipboardList, keywords: 'sdoh social needs screening ahc-hrsn prapare housing food transportation utilities safety hits questionnaire referral navigation' },
  { id: 'health-connect', label: 'Health Connect', group: 'Care Coordination', icon: Heart, keywords: 'health data integration wearable fitbit oura garmin apple health epic clinical records carin claims eob connect deep integration' },
  { id: 'tco-workflow-template-create', label: 'Configure Care Workflow', group: 'Care Coordination', icon: Workflow, keywords: 'workflow builder template create configure referral automation steps trigger outreach channel care plan member experience notifications' },

  // Community Care Hub (CCH) — grouped pages
  { id: 'cch-sponsor-console', label: 'Sponsor Console', group: 'Community Care Hub', icon: BarChart3, keywords: 'sponsor network adequacy cbo performance fund dashboard' },
  { id: 'cch-care-management', label: 'Care Management', group: 'Community Care Hub', icon: Heart, keywords: 'care plan authorization chw nemt housing discharge referral' },
  { id: 'cch-hub-admin', label: 'Hub Administration', group: 'Community Care Hub', icon: ShieldCheck, keywords: 'credentialing grievance accreditation funds flow admin' },
  { id: 'cch-reporting', label: 'Reporting & Outcomes', group: 'Community Care Hub', icon: TrendingUp, keywords: 'quality measures hedis outcomes reporting analytics' },

  // Fundraising
  { id: 'donations', label: 'Donations', group: 'Fundraising', icon: Heart, keywords: 'donation donor gift receipt 501c3 fundraising offline cash check contribution tax-deductible' },
  { id: 'donor-pipeline', label: 'Donor Pipeline', group: 'Fundraising', icon: Target, keywords: 'donor pipeline cultivation stewardship solicitation prospect fundraising major gift ask kanban' },
  { id: 'donor-events', label: 'Donor Events', group: 'Fundraising', icon: Calendar, keywords: 'donor event gala dinner webinar invitation rsvp attendance check-in fundraising guest list' },
  { id: 'donor-cohorts', label: 'Donor Cohorts', group: 'Fundraising', icon: Users, keywords: 'donor cohort segment guest list major donors board lapsed bulk invite fundraising audience list' },
  { id: 'fundraising-home', label: 'Fundraising Dashboard', group: 'Fundraising', icon: TrendingUp, keywords: 'fundraising dashboard home rollup raised ytd mtd pipeline value top donors upcoming events fees overview development goals' },

  // Marketing & Sales
  { id: 'platform-intelligence', label: 'Intelligence', group: 'Marketing & Sales', icon: Brain },
  { id: 'platform-dashboard', label: 'Platform Dashboard', group: 'Marketing & Sales', icon: BarChart3 },
  { id: 'platform-web-presence', label: 'Design Studio', group: 'Marketing & Sales', icon: Sparkles },
  { id: 'platform-marketing', label: 'Marketing Automation', group: 'Marketing & Sales', icon: Mail },
  { id: 'platform-contacts', label: 'Sales Contacts', group: 'Marketing & Sales', icon: Users },
  // Internal operator surfaces — hidden from org_admin (client-org admins). They
  // manage their own billing under Client Billing & Payments.
  { id: 'platform-sales', label: 'Sales Pipeline', group: 'Marketing & Sales', icon: Target, requiresRole: ['super_admin', 'ops_admin', 'platform_admin', 'tenant_admin'] },
  { id: 'platform-revops', label: 'RevOps', group: 'Marketing & Sales', icon: DollarSign, requiresRole: ['super_admin', 'ops_admin', 'platform_admin', 'tenant_admin'] },
  { id: 'platform-invoices', label: 'Invoices', group: 'Marketing & Sales', icon: Receipt, requiresRole: ['super_admin', 'ops_admin', 'platform_admin', 'tenant_admin'] },
  { id: 'platform-support', label: 'Customer Support', group: 'Marketing & Sales', icon: Headphones },
  { id: 'platform-visitor-id', label: 'Visitor Identification', group: 'Marketing & Sales', icon: Eye },
  { id: 'platform-cold-outreach', label: 'Cold Outreach', group: 'Marketing & Sales', icon: Send },
  { id: 'platform-lead-builder', label: 'Lead Builder', group: 'Marketing & Sales', icon: Target },
  { id: 'platform-marketplace', label: 'Marketplace', group: 'Marketing & Sales', icon: Store },

  // ─── Revenue Cycle & Claims Management ───
  // Central admin section consolidating all CalAIM/Medi-Cal billing surface area:
  // claims entry, clearinghouse, MCP rules, billing analytics, payouts.
  { id: 'billing-claims', label: 'Claims Management', group: 'Revenue Cycle & Claims Management', icon: FileText, keywords: 'claims billing 837 submission' },
  { id: 'billing-claims-new', label: 'New Claim Entry', group: 'Revenue Cycle & Claims Management', icon: FileText, keywords: 'create new claim manual entry' },
  { id: 'billing-claims-bulk', label: 'Bulk Claim Entry', group: 'Revenue Cycle & Claims Management', icon: FileText, keywords: 'bulk claims batch upload' },
  { id: 'document-processing', label: 'Document Processing', group: 'Revenue Cycle & Claims Management', icon: ScanLine, keywords: 'ocr eob document scan' },
  { id: 'billing-analytics', label: 'Billing Analytics', group: 'Revenue Cycle & Claims Management', icon: BarChart3, keywords: 'denial rate revenue analytics' },
  { id: 'program-billing', label: 'Program Billing Activities', group: 'Revenue Cycle & Claims Management', icon: Receipt, keywords: 'program billing activities cpt hcpcs configure' },
  { id: 'pmpm-billing', label: 'PMPM Billing', group: 'Revenue Cycle & Claims Management', icon: DollarSign, keywords: 'pmpm per member per month billing' },
  { id: 'clearinghouse-settings', label: 'Clearinghouse Settings', group: 'Revenue Cycle & Claims Management', icon: Network, keywords: 'stedi availity clearinghouse edi' },
  { id: 'mcp-billing-rules', label: 'MCP Billing Rules', group: 'Revenue Cycle & Claims Management', icon: Network, keywords: 'mcp medi-cal rules cs menu calaim community supports' },
  { id: 'billing-ai', label: 'Billing AI', group: 'Revenue Cycle & Claims Management', icon: Sparkles, keywords: 'braided revenue doc completeness auto-coder denial scorer ai claude llm' },
  { id: 'billing-data-sources', label: 'Public Data Sources', group: 'Revenue Cycle & Claims Management', icon: Database, keywords: 'hcpcs ncci mue leie dhcs apl lcd ncd cms public data etl' },
  { id: 'billing-ml', label: 'Billing ML', group: 'Revenue Cycle & Claims Management', icon: Brain, keywords: 'ml machine learning model training denial classifier calibrator ranker' },
  { id: 'billing-ml-synthetic-pretrain', label: 'Synthetic Pretrain', group: 'Revenue Cycle & Claims Management', icon: Brain, keywords: 'synthetic pretrain synpuf taf synthea cms desynpuf ingest m-1a m-1b m-4a braided denial cold start workstream d' },
  { id: 'otfrtf', label: 'OTF / RTF Reports', group: 'Revenue Cycle & Claims Management', icon: FileText, keywords: 'otf rtf outreach referral tracking' },
  { id: 'fund-deposits', label: 'Fund Deposits', group: 'Revenue Cycle & Claims Management', icon: DollarSign },
  { id: 'member-payouts', label: 'Member Payouts', group: 'Revenue Cycle & Claims Management', icon: Receipt },
  { id: 'reconciliation', label: 'Reconciliation', group: 'Revenue Cycle & Claims Management', icon: BarChart3 },
  { id: 'cbo-reimbursement', label: 'CBO Reimbursement', group: 'Revenue Cycle & Claims Management', icon: Receipt },
  { id: 'outreach-billing', label: 'MCP Billing Review', group: 'Revenue Cycle & Claims Management', icon: Receipt, keywords: 'mcp billing review outreach per minute flat disposition billing file la care health net supervisor approve submit' },
  { id: 'outreach-worklist', label: 'MCP Outreach Worklist', group: 'Revenue Cycle & Claims Management', icon: Receipt, keywords: 'sycamores outreach staff worklist encounter sms call disposition progress note ai document member' },
  { id: 'outreach-type-config', label: 'Outreach Types (config)', group: 'Revenue Cycle & Claims Management', icon: Receipt, keywords: 'outreach engagement disposition type configure per program mcp payer admin settings' },
  { id: 'client-billing', label: 'Client Billing & Payments', group: 'Revenue Cycle & Claims Management', icon: CreditCard, keywords: 'client billing stripe invoices payments' },
  { id: 'report-distribution', label: 'Report Distribution', group: 'Reports', icon: FileText, keywords: 'report distribution sftp delivery mco approval' },
  { id: 'ecm-progress-notes-report', label: 'Progress Notes Report', group: 'Reports', icon: FileText, keywords: 'progress notes report ecm case notes cross-member org-wide export csv member notes history' },
  { id: 'scheduled-reports', label: 'Scheduled Reports', group: 'Reports', icon: FileText, keywords: 'scheduled recurring report delivery email weekly automated send schedule distribution' },
  { id: 'pdf-templates', label: 'PDF Templates', group: 'Administration', icon: FileText, keywords: 'pdf template generate assessment care plan builder document' },

  // CBO Tools
  { id: 'terminology-glossary', label: 'Terminology Glossary', group: 'CBO Tools', icon: BookOpen },
  { id: 'team-assignments', label: 'Team Assignments', group: 'CBO Tools', icon: Users },
  { id: 'user-goals', label: 'User Goals', group: 'CBO Tools', icon: Target },

  // Workforce
  { id: 'workforce-dashboard', label: 'Workforce Dashboard', group: 'Workforce', icon: BarChart3 },
  { id: 'workforce-unified-dashboard', label: 'Unified Dashboard', group: 'Workforce', icon: LayoutDashboard },
  { id: 'workforce-candidates', label: 'Candidate Pipeline', group: 'Workforce', icon: UserPlus },
  { id: 'workforce-cases', label: 'Case Management', group: 'Workforce', icon: Briefcase },
  { id: 'workforce-roles', label: 'Workforce Roles', group: 'Workforce', icon: Users },
  { id: 'workforce-certifications', label: 'Certifications', group: 'Workforce', icon: ClipboardCheck },
  { id: 'workforce-assessments', label: 'Assessments & Exams', group: 'Workforce', icon: ListTodo },
  { id: 'workforce-curricula', label: 'Training Curricula', group: 'Workforce', icon: BookOpen },
  { id: 'workforce-learn', label: 'My Learning', group: 'Workforce', icon: Play, keywords: 'training learn enroll courses curriculum my learning' },
  // 'workforce-courses' (Classroom Courses / Google Classroom LMS) removed — the
  // LMS was deprecated in favor of the native training capabilities built for
  // LCC (Training Curricula, Training Schedule, Certifications). The route still
  // exists in app.tsx for legacy deep-links but is no longer surfaced here.
  { id: 'workforce-mentoring', label: 'Mentoring Programs', group: 'Workforce', icon: Heart },
  { id: 'workforce-training-schedule', label: 'Training Schedule', group: 'Workforce', icon: Calendar, keywords: 'training schedule series invite accept sessions workforce' },
  { id: 'workforce-roster', label: 'Workforce Roster', group: 'Workforce', icon: ClipboardList },
  { id: 'workforce-skills-gap', label: 'Skills Gap Analysis', group: 'Workforce', icon: TrendingUp },
  { id: 'workforce-ai-assistant', label: 'AI Assistant', group: 'Workforce', icon: Brain },

  // Admin
  { id: 'cbo-management', label: 'Organizations', group: 'Administration', icon: Building },
  { id: 'user-management', label: 'User Management', group: 'Administration', icon: UserCog },
  { id: 'platform-roles', label: 'Roles & Permissions', group: 'Administration', icon: Shield, keywords: 'roles permissions rbac triveny capabilities tenant_admin tenant_user' },
  { id: 'compliance-documents', label: 'Compliance Documents', group: 'Administration', icon: FileText },
  { id: 'document-config', label: 'Document Management', group: 'Administration', icon: FolderOpen },
  { id: 'hard-copy-tracking', label: 'Hard-Copy Tracking', group: 'Administration', icon: Printer },
  { id: 'letter-templates', label: 'Letter Templates', group: 'Administration', icon: FileText, keywords: 'mail postal print letter template' },
  { id: 'programs', label: 'Programs', group: 'Administration', icon: Briefcase },
  { id: 'workflows', label: 'Agent Workflow Templates', group: 'Administration', icon: Workflow },
  { id: 'form-templates', label: 'Form Builder', group: 'Administration', icon: FormInput },
  { id: 'automation-rules', label: 'Automation Rules', group: 'Administration', icon: Settings },
  { id: 'integrations', label: 'Integration Workbench', group: 'Administration', icon: ArrowLeftRight },
  // Single page: "Nightly Data Ingestion" (H1) with an "Audit Export" section
  // beneath it. Audit-export keywords are folded in so search still finds it
  // without a second row pointing at the same tab.
  { id: 'data-ingestion', label: 'Nightly Data Ingestion', group: 'Administration', icon: Database, keywords: 'sftp nightly member file ingest validation report fhir patient coverage audit export hipaa csv json compliance logs' },
  { id: 'admin', label: 'Program & Provider Import', group: 'Administration', icon: Database, keywords: 'program import provider import cbo import etl pipeline data import enrichment ml data sources plan comparison intent sources' },
  { id: 'external-services', label: 'External Services Catalog', group: 'Administration', icon: Zap, keywords: 'external services catalog api keys' },
  { id: 'cbo-data-entry', label: 'CBO Data Entry (ML Feature Store)', group: 'Administration', icon: FormInput, keywords: 'cbo data entry ml feature store' },
  { id: 'access-control', label: 'Roles & Permissions', group: 'Administration', icon: Shield, keywords: 'rbac roles permissions access control' },
  { id: 'break-glass-management', label: 'Break Glass Audit', group: 'Administration', icon: AlertTriangle, keywords: 'break glass emergency access audit' },
  { id: 'external-systems', label: 'External Systems', group: 'Administration', icon: Network, keywords: 'external systems integrations directory' },
  { id: 'field-sensitivity-settings', label: 'Field Privacy Config', group: 'Administration', icon: Shield, keywords: 'field privacy sensitivity phi pii' },
  { id: 'ocr-template-settings', label: 'OCR & Card Templates', group: 'Administration', icon: ScanLine, keywords: 'ocr card templates document scanning' },
  { id: 'organization-setup-wizard', label: 'Organization Setup Wizard', group: 'Administration', icon: Building, keywords: 'org onboarding setup wizard' },
  { id: 'self-signup', label: 'Self-Signup Dashboard', group: 'Administration', icon: UserPlus, keywords: 'self service signup public registration' },
  { id: 'viewer-capabilities', label: 'Platform Capabilities', group: 'Administration', icon: Eye, keywords: 'viewer capabilities platform' },
  { id: 'form-analytics', label: 'Form Analytics', group: 'Administration', icon: BarChart3, keywords: 'form analytics submissions metrics' },
  { id: 'ihe-config', label: 'IHE Document Exchange', group: 'Administration', icon: Share2, keywords: 'ihe document exchange xds health information' },
  { id: 'organization-roles', label: 'Organization Roles', group: 'Administration', icon: Shield },
  { id: 'feature-capabilities', label: 'Feature Capabilities', group: 'Administration', icon: Settings },
  { id: 'page-capabilities', label: 'Page Capabilities', group: 'Administration', icon: Settings },
  { id: 'role-page-access', label: 'Role Page Access', group: 'Administration', icon: UserCog },
  { id: 'nav-layout', label: 'Navigation Layout', group: 'Administration', icon: LayoutDashboard },
  { id: 'data-privacy-settings', label: 'Data Privacy & Shield', group: 'Administration', icon: Shield },
  { id: 'communication-templates', label: 'Communication Templates', group: 'Administration', icon: MessageSquare },
  { id: 'campaign-templates', label: 'Campaign Templates', group: 'Administration', icon: MessageSquare },
  { id: 'template-variables', label: 'Template Variables', group: 'Administration', icon: Braces, keywords: 'template variables merge fields placeholders personalization custom inputs letters forms messages' },
  { id: 'sender-verification', label: 'Sender Verification', group: 'Administration', icon: Shield },
  { id: 'member-fields', label: 'Field Configuration', group: 'Administration', icon: FormInput, keywords: 'fields list columns advanced filters quick filter chips configure show in list' },
  // Staff Profiles is a palette-only admin page like Field Configuration and
  // Organization Roles — its nav_config seed (mig 464) uses an empty section so
  // it never renders in the sidebar, and unlike its siblings it had no palette
  // row, leaving org admins with no way to reach the staff roster/edit screen
  // (TRUS-548). Access is enforced at render by tabAccessRegistry (ADMIN_ROLES).
  { id: 'staff-profiles', label: 'Staff Profiles', group: 'Administration', icon: UserCog, keywords: 'staff profiles roster employees team members view edit access level trainings training assignments compliance' },
  { id: 'field-mapping', label: 'Field Mapping', group: 'Administration', icon: GitBranch },
  { id: 'translation-management', label: 'Translation Management', group: 'Administration', icon: Globe },
  { id: 'rag-admin', label: 'RAG Administration', group: 'Administration', icon: Brain },
  { id: 'public-links', label: 'Public Links', group: 'Administration', icon: Globe },
  { id: 'saml-sso-config', label: 'SSO Configuration', group: 'Administration', icon: Shield },
  { id: 'hl7-config', label: 'HL7 Messaging', group: 'Administration', icon: Network },
  { id: 'ecm-compliance', label: 'Compliance & Notifications', group: 'Administration', icon: ShieldCheck },
  { id: 'cbo-compliance-dashboard', label: 'Work Compliance', group: 'Administration', icon: Target },
  { id: 'security-compliance', label: 'Security & Compliance', group: 'Administration', icon: Shield },
  { id: 'ai-orchestration', label: 'AI Orchestration Center', group: 'Administration', icon: Brain },
  { id: 'config-migration', label: 'Configuration Migration', group: 'Administration', icon: ArrowLeftRight },

  // Incentives & Outcomes
  { id: 'actions', label: 'Action Tracker', group: 'Incentives', icon: Target },
  { id: 'member-milestone-progress', label: 'Member Milestone Progress', group: 'Incentives', icon: Gift },
  { id: 'pending-milestone-approvals', label: 'Pending Approvals', group: 'Incentives', icon: DollarSign },
  { id: 'milestone-management', label: 'Manage Milestone Types', group: 'Incentives', icon: Target, keywords: 'milestone types incentives' },
  { id: 'program-milestone-config', label: 'Configure Program Milestones', group: 'Incentives', icon: Target, keywords: 'program milestones incentives config' },
  { id: 'sponsor-terms', label: 'Sponsor Incentive Terms', group: 'Incentives', icon: FileText, keywords: 'sponsor incentive terms agreements' },
  { id: 'card-management', label: 'Card Management', group: 'Payments', icon: CreditCard },
  { id: 'transactions', label: 'Transaction History', group: 'Payments', icon: Receipt },
  { id: 'my-card', label: 'My Card Portal', group: 'Payments', icon: CreditCard, keywords: 'my card member portal balance' },
  { id: 'my-phone', label: 'My Phone Number', group: 'Account', icon: Phone, keywords: 'my phone number verify caller id byo own number sms' },

  // Employer
  { id: 'employee-retention', label: 'Benefits Analytics', group: 'Employer', icon: BarChart3 },
  { id: 'employer-workflow-orchestration', label: 'Enrollment & Service Referrals', group: 'Employer', icon: Play },
  { id: 'employer-benefits-management', label: 'Benefits & Enrollments', group: 'Employer', icon: Gift },

  // Developer
  { id: 'developer-portal', label: 'API Docs & Sandbox', group: 'Developer', icon: Code },
  { id: 'sandbox-management', label: 'Sandbox Management', group: 'Developer', icon: Building },
  { id: 'test-runner', label: 'Test Runner', group: 'Developer', icon: PlayCircle },
];

/**
 * Human name + area for a tab id (the app's `activeTab`).
 *
 * This page catalog is the only complete tab-id → label map in the app, so the
 * support widget reads it to report WHICH SCREEN a ticket came from. Without it
 * the widget guessed from the DOM and named the page underneath whatever the
 * user was actually working in. Returns null for ids not in the catalog.
 */
export function getPageDescriptor(tabId: string): { label: string; group: string } | null {
  if (!tabId) return null;
  const page = NAVIGABLE_PAGES.find((p) => p.id === tabId);
  return page ? { label: page.label, group: page.group } : null;
}

/** Who is looking: everything the palette's per-page gating reads. */
export interface NavigablePageContext {
  isTriveny: boolean;
  userRole?: string | null;
  activeOrgId: number | null;
  /** The org's `social_needs_screening` capability (useCapability). */
  screenerCapEnabled: boolean;
  /**
   * The org's `ffs_transition` capability (TRUS-739). Off unless the org turned
   * it on; missing/undefined hides the FFS transition cohort page.
   */
  ffsTransitionCapEnabled?: boolean;
}

/**
 * The pages the palette offers this user, in catalog order: Triveny shows only
 * its platform pages, WeaveIn hides Triveny-only twins, and per-entry role / org
 * / pilot / capability gates are honoured (the sidebar filters its own items by
 * access; these entries are often palette-only, so the gating lives here).
 */
export function filterNavigablePages(ctx: NavigablePageContext): NavigablePage[] {
  return NAVIGABLE_PAGES.filter((page) => {
    if (ctx.isTriveny ? !TRIVENY_PALETTE_PAGE_IDS.has(page.id) : WEAVEIN_HIDDEN_TAB_PAGE_IDS.has(page.id)) return false;
    if (page.requiresRole && page.requiresRole.length > 0) {
      if (!ctx.userRole || !page.requiresRole.includes(ctx.userRole)) return false;
    }
    if (page.requiresOrg && page.requiresOrg.length > 0) {
      if (ctx.activeOrgId == null || !page.requiresOrg.includes(ctx.activeOrgId)) return false;
    }
    // Behavioral Assessment pilot: allowlisted orgs only (same gate as app.tsx).
    if (page.id === 'behavioral-assessment' && !isBehavioralAssessmentAllowed(ctx.activeOrgId, null)) return false;
    // Social Needs Screener: gated by the social_needs_screening capability.
    if (page.id === 'social-needs-screener' && !ctx.screenerCapEnabled) return false;
    // FFS transition cohort (TRUS-739): only orgs that turned ffs_transition on.
    if (page.id === 'ffs-transition-cohort' && !ctx.ffsTransitionCapEnabled) return false;
    return true;
  });
}

/**
 * Where selecting a catalog page actually goes. Triveny has no healthcare CBO
 * directory; its "Organizations" is the operator directory at org-settings.
 */
export function navigablePageTarget(id: string, isTriveny: boolean): string {
  return isTriveny && id === 'cbo-management' ? 'org-settings' : id;
}

// Duplicate ids are always a bug (two rows that navigate to the same tab). Fail
// loudly in dev; in production only log, so a stray dup never white-screens.
(function assertNavigablePagesIntegrity() {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  const labels = new Set<string>();
  const dupLabels = new Set<string>();
  for (const p of NAVIGABLE_PAGES) {
    if (seen.has(p.id)) dupes.add(p.id);
    seen.add(p.id);
    if (labels.has(p.label)) dupLabels.add(p.label);
    labels.add(p.label);
  }
  const isDev = import.meta.env.DEV;
  if (dupes.size) {
    const msg = `[navigablePages] registry integrity error — duplicate page id(s): ${[...dupes].join(', ')}`;
    if (isDev) throw new Error(msg);
    // eslint-disable-next-line no-console
    console.error(msg);
  }
  if (dupLabels.size && isDev) {
    // eslint-disable-next-line no-console
    console.warn(`[navigablePages] duplicate labels (only expected for WeaveIn/Triveny twins): ${[...dupLabels].join(', ')}`);
  }
})();
